import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const ADMIN_KEY_STORAGE = 'mgn_admin_key';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState('');
  const [isAuthed, setIsAuthed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem(ADMIN_KEY_STORAGE);
    if (saved) {
      setAdminKey(saved);
      authenticateAdmin(saved);
    }
  }, []);

  const authenticateAdmin = async (key) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stats?admin_key=${encodeURIComponent(key)}`
      );
      setStats(data);
      setIsAuthed(true);
      localStorage.setItem(ADMIN_KEY_STORAGE, key);
      fetchWithdrawals(key);
      fetchUsers(key);
    } catch (err) {
      setError('Invalid admin key');
      setIsAuthed(false);
      localStorage.removeItem(ADMIN_KEY_STORAGE);
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawals = async (key) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/withdrawals?admin_key=${encodeURIComponent(key || adminKey)}`
      );
      setWithdrawals(data.withdrawals);
    } catch (err) { /* ignore */ }
  };

  const fetchUsers = async (key) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?admin_key=${encodeURIComponent(key || adminKey)}`
      );
      setUsers(data.users);
    } catch (err) { /* ignore */ }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/stats?admin_key=${encodeURIComponent(adminKey)}`
      );
      setStats(data);
    } catch (err) { /* ignore */ }
  };

  const markComplete = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/withdrawals/${id}/complete?admin_key=${encodeURIComponent(adminKey)}`
      );
      fetchWithdrawals();
      fetchStats();
    } catch (err) {
      setError('Failed to update withdrawal');
    } finally {
      setActionLoading('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
    setIsAuthed(false);
    setAdminKey('');
    setStats(null);
    setWithdrawals([]);
    setUsers([]);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-[#121214] border border-white/10 rounded-2xl p-8">
            <h2 className="text-3xl font-medium mb-2 tracking-tight text-center">Admin Access</h2>
            <p className="text-[#A1A1A6] mb-8 text-center">Enter admin key to continue</p>

            {error && (
              <div className="mb-6 bg-[#FF453A]/10 border border-[#FF453A]/20 rounded-lg p-3 text-[#FF453A] text-sm" data-testid="admin-error">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && authenticateAdmin(adminKey)}
                placeholder="Enter admin key"
                data-testid="admin-key-input"
                className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-lg p-3 text-white outline-none"
              />
              <button
                onClick={() => authenticateAdmin(adminKey)}
                disabled={loading || !adminKey}
                data-testid="admin-login-button"
                className="w-full px-6 py-4 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full font-medium transition-all disabled:opacity-50 text-lg"
              >
                {loading ? 'Verifying...' : 'Access Dashboard'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'withdrawals', label: 'Withdrawals' },
    { id: 'users', label: 'Users' },
  ];

  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');
  const completedWithdrawals = withdrawals.filter(w => w.status === 'completed');

  return (
    <div className="min-h-screen bg-black py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-medium tracking-tight">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="px-6 py-2.5 bg-white/10 hover:bg-white/20 rounded-full text-sm font-medium transition-all"
          >
            Exit Admin
          </button>
        </div>

        <div className="flex gap-2 mb-8 border-b border-white/10 pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#FF3B30] text-white'
                  : 'bg-white/5 text-[#A1A1A6] hover:bg-white/10'
              }`}
            >
              {tab.label}
              {tab.id === 'withdrawals' && pendingWithdrawals.length > 0 && (
                <span className="ml-2 bg-white/20 text-white px-2 py-0.5 rounded-full text-xs">
                  {pendingWithdrawals.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-6 bg-[#FF453A]/10 border border-[#FF453A]/20 rounded-lg p-3 text-[#FF453A] text-sm">
            {error}
          </div>
        )}

        {activeTab === 'overview' && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Users" value={stats.total_users} color="#0A84FF" testId="stat-total-users" />
              <StatCard label="Registered" value={stats.registered_users} color="#32D74B" testId="stat-registered" />
              <StatCard label="Paid Users" value={stats.paid_users} color="#FF9F0A" testId="stat-paid" />
              <StatCard label="Revenue" value={`₹${stats.total_revenue.toFixed(0)}`} color="#FF3B30" testId="stat-revenue" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard label="Pending Withdrawals" value={stats.pending_withdrawals} subtitle={`₹${stats.pending_amount.toFixed(0)}`} color="#FF9F0A" testId="stat-pending-wd" />
              <StatCard label="Completed Withdrawals" value={stats.completed_withdrawals} subtitle={`₹${stats.completed_amount.toFixed(0)}`} color="#32D74B" testId="stat-completed-wd" />
              <StatCard label="Total Payments" value={stats.total_payments} color="#0A84FF" testId="stat-total-payments" />
            </div>
          </motion.div>
        )}

        {activeTab === 'withdrawals' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {pendingWithdrawals.length > 0 && (
              <div>
                <h3 className="text-xl font-medium mb-4 text-[#FF9F0A]">Pending ({pendingWithdrawals.length})</h3>
                <div className="bg-[#121214] border border-white/10 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10 text-left text-[#A1A1A6] text-sm">
                          <th className="px-6 py-4">Name</th>
                          <th className="px-6 py-4">Email</th>
                          <th className="px-6 py-4">UPI ID</th>
                          <th className="px-6 py-4">Amount</th>
                          <th className="px-6 py-4">Requested</th>
                          <th className="px-6 py-4">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingWithdrawals.map((w) => (
                          <tr key={w._id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="px-6 py-4 font-medium">{w.name || w.username}</td>
                            <td className="px-6 py-4 text-[#A1A1A6] text-sm">{w.email}</td>
                            <td className="px-6 py-4">
                              <span className="bg-[#0A84FF]/10 text-[#0A84FF] px-3 py-1 rounded-lg text-sm font-medium" data-testid={`upi-${w._id}`}>
                                {w.upi_id}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-[#32D74B] font-medium">₹{w.amount.toFixed(2)}</td>
                            <td className="px-6 py-4 text-[#A1A1A6] text-sm">{formatDate(w.requested_at)}</td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => markComplete(w._id)}
                                disabled={actionLoading === w._id}
                                data-testid={`complete-${w._id}`}
                                className="px-4 py-2 bg-[#32D74B] hover:bg-[#2AB840] rounded-full text-sm font-medium text-black transition-all disabled:opacity-50"
                              >
                                {actionLoading === w._id ? 'Updating...' : 'Mark Paid'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-xl font-medium mb-4 text-[#32D74B]">Completed ({completedWithdrawals.length})</h3>
              {completedWithdrawals.length === 0 ? (
                <p className="text-[#A1A1A6] text-center py-8">No completed withdrawals yet</p>
              ) : (
                <div className="bg-[#121214] border border-white/10 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10 text-left text-[#A1A1A6] text-sm">
                          <th className="px-6 py-4">Name</th>
                          <th className="px-6 py-4">Email</th>
                          <th className="px-6 py-4">UPI ID</th>
                          <th className="px-6 py-4">Amount</th>
                          <th className="px-6 py-4">Requested</th>
                          <th className="px-6 py-4">Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completedWithdrawals.map((w) => (
                          <tr key={w._id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="px-6 py-4 font-medium">{w.name || w.username}</td>
                            <td className="px-6 py-4 text-[#A1A1A6] text-sm">{w.email}</td>
                            <td className="px-6 py-4 text-sm">{w.upi_id}</td>
                            <td className="px-6 py-4 text-[#A1A1A6]">₹{w.amount.toFixed(2)}</td>
                            <td className="px-6 py-4 text-[#A1A1A6] text-sm">{formatDate(w.requested_at)}</td>
                            <td className="px-6 py-4 text-[#32D74B] text-sm">{formatDate(w.completed_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {withdrawals.length === 0 && (
              <p className="text-[#A1A1A6] text-center py-12">No withdrawal requests yet</p>
            )}
          </motion.div>
        )}

        {activeTab === 'users' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="bg-[#121214] border border-white/10 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-[#A1A1A6] text-sm">
                      <th className="px-6 py-4">Name</th>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Phone</th>
                      <th className="px-6 py-4">Blood Group</th>
                      <th className="px-6 py-4">Vehicle 1</th>
                      <th className="px-6 py-4">Registered</th>
                      <th className="px-6 py-4">Paid</th>
                      <th className="px-6 py-4">Wallet</th>
                      <th className="px-6 py-4">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u._id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-6 py-4 font-medium">{u.name || u.username || '-'}</td>
                        <td className="px-6 py-4 text-[#A1A1A6] text-sm">{u.email}</td>
                        <td className="px-6 py-4 text-sm">{u.phone_number || '-'}</td>
                        <td className="px-6 py-4">
                          <span className="text-[#FF3B30] font-medium">{u.blood_group || '-'}</span>
                        </td>
                        <td className="px-6 py-4 text-sm">{u.vehicle1_number || '-'}</td>
                        <td className="px-6 py-4">
                          <StatusBadge active={u.is_registered} />
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge active={u.is_paid} />
                        </td>
                        <td className="px-6 py-4 text-[#32D74B]">₹{(u.wallet_balance || 0).toFixed(0)}</td>
                        <td className="px-6 py-4 text-[#A1A1A6] text-sm">{formatDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {users.length === 0 && (
                <p className="text-[#A1A1A6] text-center py-12">No users yet</p>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, subtitle, color, testId }) => (
  <div className="bg-[#121214] border border-white/10 rounded-2xl p-6" data-testid={testId}>
    <p className="text-[#A1A1A6] text-sm mb-2">{label}</p>
    <p className="text-3xl font-medium" style={{ color }}>{value}</p>
    {subtitle && <p className="text-[#A1A1A6] text-sm mt-1">{subtitle}</p>}
  </div>
);

const StatusBadge = ({ active }) => (
  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
    active ? 'bg-[#32D74B]/10 text-[#32D74B]' : 'bg-white/5 text-[#A1A1A6]'
  }`}>
    {active ? 'Yes' : 'No'}
  </span>
);

export default AdminDashboard;
